/**
 * Establishment Service Hooks
 *
 * React Query hooks for establishment operations
 */

import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { authService, establishmentService, backofficeService } from '../services/api';
import { ESTABLISHMENT_KEYS } from './keys';

import type {
  // Establishment types
  Establishment,
  CreateEstablishmentRequest,
  UpdateEstablishmentRequest,
  EstablishmentListResponse,
  EstablishmentSearchParams,
  EstablishmentUserSearchParams,

  // User types
  EstablishmentUser,
  CreateEstablishmentUserRequest,
  UpdateEstablishmentUserRequest,
  DeleteEstablishmentUserRequest,
  EstablishmentUserListResponse,

  // Interval types
  EstablishmentInterval,
  CreateIntervalRequest,
  UpdateIntervalRequest,
  IntervalListResponse,

  // Class types
  EstablishmentClass,
  UpdateClassRequest,
  ClassListResponse,

  // Menu types
  EstablishmentMenu,
  CreateMenuRequest,
  UpdateMenuRequest,
  MenuPhotoRequest,
  MenuListResponse,
  MenuListParams,

  // Product types
  EstablishmentProduct,
  CreateEstablishmentProductRequest,
  UpdateEstablishmentProductRequest,
  UpdateEstablishmentProductStatusRequest,
  LinkProductsBatchRequest,
  EstablishmentProductListResponse,
  EstablishmentProductDetailResponse,
  EstablishmentProductSuggestionsResponse,
  EstablishmentProductSearchParams,
  GlobalProductSearchParams,

  // Stock types
  StockItem,
  AddStockRequest,
  ScheduleStockTransferRequest,
  ExecuteStockTransferRequest,
  CancelStockTransferRequest,
  StockListResponse,
  StockTransferListResponse,
  StockListParams,
  StockTransferParams,

  // Extract types
  EstablishmentExtractParams,
  EstablishmentExtractResponse,
  ExtractItem,
  ExtractBalance,
} from '../services/api';
import type {
  Supplier,
  SupplierListParams,
  SupplierListResponse,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  DeleteSupplierRequest,
  DeleteSupplierResponse,
  TaxOption,
} from '@services/api/establishmentTypes';
import type {
  AttachMenuRequest,
  CreateClassRequest,
  DetachMenuRequest,
  EstabilishmentProductDetailsParams,
  EstablishmentLunchboxDashboard,
  EstablishmentLunchboxDashboardParams,
  EstablishmentLunchboxClassDetail,
  EstablishmentLunchboxClassDetailParams,
  GetDashboardDataParams,
  GetEstablishmentExtractParams,
  GetEstablishmentExtractResponse,
  GetEstablishmentSalesQuery,
  GetEstablishmentSalesResponse,
  ProductListAllResponse,
  UpdateEstablishmentProductRequestParams,
  GetEstablishmentWithdrawsHistoryParams,
  GetEstablishmentWithdrawsHistoryResponse,
  StockSummaryParams,
  StockSummaryResponse,
  StockMovementsParams,
  StockMovementsResponse,
  StockExpirationParams,
  StockExpirationResponse,
  GetEstablishmentCashFlowSummaryParams,
  GetEstablishmentCashFlowSummaryResponse,
  GetEstablishmentCashFlowMovementsParams,
  GetEstablishmentCashFlowMovementsResponse,
  EstablishmentOperatorListResponse,
  CreateStockOutRequest,
  CreateStockEntryRequest,
  StockNFsParams,
  StockNFsResponse,
  StockTransferRequest,
  EstablishmentBalance,
  CreateEstablishmentInternalUserRequest,
  InternalMembershipRequest,
  PostEstablishmentWithdrawsDto,
  PostEstablishmentWithdrawsResponse,
  WithdrawConfirmDto,
  WithdrawConfirmResponse,
  WithdrawInitDto,
  WithdrawInitResponse,
  WithdrawDeleteDto,
  WithdrawDeleteResponse,
  GetOrdersReportOverallParams,
  GetOrdersReportOverallResponse,
  GetOrdersReportOverallByIntervalParams,
  GetOrdersReportOverallByIntervalResponse,
  GetOrdersReportByAttendantParams,
  GetOrdersReportByAttendantResponse,
  GetOrdersReportPctUnderTargetParams,
  GetOrdersReportPctUnderTargetResponse,
  CreateEstablishmentFromAdminRequest,
  TaxIntegrationStatus,
  EnableTaxIntegrationRequest,
  ConfigureTaxIntegrationRequest,
  DeleteTaxIntegrationRequest,
  DeleteTaxCertificateRequest,
  UploadTaxCertificateRequest,
  TaxConfigResponse,
  TaxCstListResponse,
  EstablishmentTotemBillingSummary,
  PaytimeInfo,
  PayPurchaseRequest,
  PayPurchaseResponse,
  PurchasePixInitRequest,
  PurchasePixInitResponse,
  PurchasePixConfirmRequest,
  PurchasePixConfirmResponse,
  PaymentReceipt,
} from '@services/api/establishmentTypes';

// ==================== ESTABLISHMENT MUTATIONS ====================

/**
 * Hook para criar estabelecimento
 */
export function useCreateEstablishment(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_ESTABLISHMENT],
    mutationFn: (data: CreateEstablishmentRequest) => establishmentService.createEstablishment(data),
    onSuccess: () => {
      // Invalidate establishment lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENTS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar estabelecimento:', error);
    },
    ...options,
  });
}

/**
 * Hook para atualizar estabelecimento
 */
export function useUpdateEstablishment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_ESTABLISHMENT],
    mutationFn: (data: UpdateEstablishmentRequest) => establishmentService.updateEstablishment(data),
    onSuccess: (data: Establishment) => {
      // Invalidate specific establishment and lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT, data.id] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENTS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar estabelecimento:', error);
    },
  });
}

// ==================== ESTABLISHMENT QUERIES ====================

/**
 * Hook para obter estabelecimento por ID
 */
export function useEstablishment(establishmentId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT, establishmentId],
    queryFn: () => establishmentService.getEstablishmentById(establishmentId),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para listar tipos de estabelecimento
 */
export function useEstablishmentTypes() {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_TYPES],
    queryFn: () => establishmentService.listEstablishmentTypes(),
    staleTime: 10 * 60 * 1000, // 10 minutes - types don't change often
  });
}

/**
 * Hook para listar bancos
 */
export function useBanks() {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_BANKS],
    queryFn: () => establishmentService.listBanks(),
    staleTime: 10 * 60 * 1000, // 10 minutes - banks don't change often
  });
}

/**
 * Hook para listar estabelecimentos
 */
export function useEstablishments(params?: EstablishmentSearchParams, enabled: boolean = true) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENTS, params],
    queryFn: () => establishmentService.listEstablishments(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    enabled,
  });
}

/**
 * Hook para obter estabelecimentos por instituição
 */
export function useEstablishmentsByInstitution(institutionId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENTS_BY_INSTITUTION, institutionId],
    queryFn: () => establishmentService.getEstablishmentsByInstitution(institutionId),
    enabled: !!institutionId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para verificar se estabelecimento existe
 */
export function useEstablishmentExists(establishmentId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.ESTABLISHMENT_EXISTS, establishmentId],
    queryFn: () => establishmentService.establishmentExists(establishmentId),
    enabled: !!establishmentId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook para obter todos os estabelecimentos (para dropdowns/select)
 */
export function useAllEstablishments() {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_ALL_ESTABLISHMENTS],
    queryFn: () => establishmentService.getAllEstablishments(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ==================== USER MUTATIONS ====================

/**
 * Hook para editar usuário do estabelecimento
 */
export function useEditEstablishmentUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.EDIT_ESTABLISHMENT_USER],
    mutationFn: (data: UpdateEstablishmentUserRequest) => establishmentService.editEstablishmentUser(data),
    onSuccess: () => {
      // Invalidate user lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_USERS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao editar usuário do estabelecimento:', error);
    },
  });
}

/**
 * Hook para deletar usuário do estabelecimento
 */
export function useDeleteEstablishmentUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.DELETE_ESTABLISHMENT_USER],
    mutationFn: (data: DeleteEstablishmentUserRequest) => establishmentService.deleteEstablishmentUser(data),
    onSuccess: () => {
      // Invalidate user lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_USERS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao deletar usuário do estabelecimento:', error);
    },
  });
}

/**
 * Hook para criar usuário do estabelecimento
 */
export function useCreateEstablishmentInternalUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_ESTABLISHMENT_INTERNAL_USER],
    mutationFn: (data: CreateEstablishmentInternalUserRequest) => establishmentService.createEstablishmentInternalUser(data),
    onSuccess: () => {
      // Invalidate user lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_USERS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar usuário do estabelecimento:', error);
    },
  });
}

/**
 * Hook para verificar se usuário interno existe por CPF
 * GET /user/internal/check?cpf=CPF
 */
export function useCheckInternalUserByCpf() {
  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CHECK_INTERNAL_USER_BY_CPF],
    mutationFn: (cpf: string) => establishmentService.checkInternalUserByCpf(cpf),
  });
}

/**
 * Hook para incluir usuário interno no estabelecimento (membership)
 * POST /user/internal/membership
 */
export function useAddInternalMembership() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.ADD_INTERNAL_MEMBERSHIP],
    mutationFn: (data: InternalMembershipRequest) => establishmentService.addInternalMembership(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_USERS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao incluir usuário no estabelecimento:', error);
    },
  });
}

// ==================== USER QUERIES ====================

/**
 * Hook para listar usuários do estabelecimento atual
 */
export function useEstablishmentUsers(establishmentId: string, options?: any) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ESTABLISHMENT_USERS, establishmentId],
    queryFn: () => establishmentService.listEstablishmentUsers(establishmentId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook para listar todos os usuários de um estabelecimento
 */
export function useAllEstablishmentUsers(establishmentId: string, params?: EstablishmentUserSearchParams) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS, establishmentId, params],
    queryFn: () => establishmentService.listAllEstablishmentUsers(establishmentId, params),
    enabled: !!establishmentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook para listar todos os operadores de um estabelecimento
 */
export function useAllEstablishmentOperators(establishmentId: string) {
  return useQuery<EstablishmentOperatorListResponse[]>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_OPERATORS, establishmentId],
    queryFn: () => establishmentService.listAllEstablishmentOperators(establishmentId),
    enabled: !!establishmentId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

export function useGetAllEstablishmentUsers(establishmentId: string, options?: any) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_ALL_ESTABLISHMENT_USERS, establishmentId],
    queryFn: () => establishmentService.getAllEstablishmentUsers(establishmentId),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// ==================== INTERVAL MUTATIONS ====================

/**
 * Hook para criar intervalo
 */
export function useCreateInterval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_INTERVAL],
    mutationFn: (data: CreateIntervalRequest) => establishmentService.createInterval(data),
    onSuccess: (data: EstablishmentInterval) => {
      // Invalidate intervals list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_INTERVALS, data.establishment_id] });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar intervalo:', error);
    },
  });
}

/**
 * Hook para atualizar intervalo
 */
export function useUpdateInterval() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_INTERVAL],
    mutationFn: (data: UpdateIntervalRequest) => establishmentService.updateInterval(data),
    onSuccess: (data: EstablishmentInterval) => {
      // Invalidate intervals list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_INTERVALS, data.establishment_id] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar intervalo:', error);
    },
  });
}

// ==================== INTERVAL QUERIES ====================

/**
 * Hook para listar intervalos de um estabelecimento
 */
export function useIntervals(establishmentId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_INTERVALS, establishmentId],
    queryFn: () => establishmentService.listIntervals(establishmentId),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ==================== CLASS MUTATIONS ====================

/**
 * Hook para atualizar turma
 */

export function useCreateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_CLASS],
    mutationFn: (data: CreateClassRequest) => establishmentService.createClass(data),
    onSuccess: (data: EstablishmentClass) => {
      // Invalidate classes list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_CLASSES, data.establishment_id] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar turma:', error);
    },
  });
}

export function useUpdateClass() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_CLASS],
    mutationFn: (data: UpdateClassRequest) => establishmentService.updateClass(data),
    onSuccess: (data: EstablishmentClass) => {
      // Invalidate classes list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_CLASSES, data.establishment_id] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar turma:', error);
    },
  });
}

// ==================== CLASS QUERIES ====================

/**
 * Hook para listar turmas de um estabelecimento
 */
export function useClasses(establishmentId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_CLASSES, establishmentId],
    queryFn: () => establishmentService.listClasses(establishmentId),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ==================== MENU MUTATIONS ====================

/**
 * Hook para criar cardápio
 */
export function useCreateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_MENU],
    mutationFn: (data: CreateMenuRequest) => establishmentService.createMenu(data),
    onSuccess: () => {
      // Invalidate menus list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar cardápio:', error);
    },
  });
}

/**
 * Hook para fazer upload de foto do cardápio
 */
export function useUploadMenuPhoto() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPLOAD_MENU_PHOTO],
    mutationFn: ({ menuId, data }: { menuId: string; data: MenuPhotoRequest }) => establishmentService.uploadMenuPhoto(menuId, data),
    onSuccess: () => {
      // Invalidate menus list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao fazer upload da foto do cardápio:', error);
    },
  });
}

/**
 * Hook para atualizar cardápio
 */
export function useUpdateMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_MENU],
    mutationFn: (data: UpdateMenuRequest) => establishmentService.updateMenu(data),
    onSuccess: () => {
      // Invalidate menus list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar cardápio:', error);
    },
  });
}

/**
 * Hook para associar cardápio ao estabelecimento
 */
export function useBackofficeAttachMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.BACKOFFICE_ATTACH_MENU],
    mutationFn: (data: AttachMenuRequest) => establishmentService.attachMenu(data),
    onSuccess: () => {
      // Invalidate menus list to refresh the data
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao associar cardápio:', error);
    },
  });
}

export function useAttachMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.ATTACH_MENU],
    mutationFn: (data: AttachMenuRequest) => establishmentService.attachMenu(data),
    onSuccess: () => {
      // Invalidate menus list to refresh the data
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao associar cardápio:', error);
    },
  });
}

/**
 * Hook para remover cardápio do estabelecimento
 */
export function useDetachMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.DETACH_MENU],
    mutationFn: (data: DetachMenuRequest) => establishmentService.detachMenu(data),
    onSuccess: () => {
      // Invalidate menus list to refresh the data
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao remover cardápio:', error);
    },
  });
}

export function useBackofficeDetachMenu() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.BACKOFFICE_DETACH_MENU],
    mutationFn: (data: DetachMenuRequest) => establishmentService.detachMenu(data),
    onSuccess: () => {
      // Invalidate menus list to refresh the data
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao remover cardápio:', error);
    },
  });
}

// ==================== MENU QUERIES ====================

/**
 * Hook para listar cardápios
 */
export function useMenus(params: MenuListParams, options?: any) {
  return useQuery<MenuListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_MENUS, params],
    queryFn: () => establishmentService.listMenusBackoffice(params),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para obter cardápios com produtos de um estabelecimento
 */
export function useMenusWithProducts(establishmentId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_MENUS_WITH_PRODUCTS, establishmentId],
    queryFn: () => establishmentService.getEstablishmentMenusWithProducts(establishmentId),
    enabled: !!establishmentId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para obter produtos por cardápio
 */
export function useProductsByMenu(establishmentId: string, menuId: string, options?: any) {
  return useQuery<MenuListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_PRODUCTS_BY_MENU, establishmentId, menuId],
    queryFn: () => establishmentService.getProductsByMenu(establishmentId, menuId),
    enabled: !!establishmentId && !!menuId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para obter produtos com preços por turma
 */
export function useProductsWithClassPricing(establishmentId: string, menuId: string, classId: string) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_PRODUCTS_WITH_CLASS_PRICING, establishmentId, menuId, classId],
    queryFn: () => establishmentService.getProductsWithClassPricing(establishmentId, menuId, classId),
    enabled: !!establishmentId && !!menuId && !!classId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// ==================== PRODUCT MUTATIONS ====================

/**
 * Hook para criar produto do estabelecimento
 */
export function useCreateEstablishmentProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_PRODUCT],
    mutationFn: (data: CreateEstablishmentProductRequest) => establishmentService.createProduct(data),
    onSuccess: () => {
      // Invalidate products list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao criar produto:', error);
    },
  });
}

/**
 * Hook para atualizar produto do estabelecimento
 */
export function useUpdateEstablishmentProduct() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_PRODUCT],
    mutationFn: ({ data, params }: { data: UpdateEstablishmentProductRequest; params: UpdateEstablishmentProductRequestParams }) =>
      establishmentService.updateProduct(data, params),
    onSuccess: () => {
      // Invalidate specific product and list
      // Note: EstablishmentProduct doesn't expose an 'id' field in types; invalidate general lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar produto:', error);
    },
  });
}

/**
 * Hook para atualizar status do produto
 */
export function useUpdateProductStatus(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_PRODUCT_STATUS],
    mutationFn: (data: UpdateEstablishmentProductStatusRequest) => establishmentService.updateProductStatus(data),
    onSuccess: () => {
      // Invalidate products list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao atualizar status do produto:', error);
    },
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para vincular vários produtos em uma chamada (falha parcial por item)
 */
export function useLinkProductsBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.LINK_PRODUCTS_BATCH],
    mutationFn: (data: LinkProductsBatchRequest) => establishmentService.linkProductsBatch(data),
    onSuccess: (data) => {
      // Invalidate products list se ao menos um item foi vinculado
      if (data.success_count > 0) {
        queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS] });
      }
    },
    onError: (error: Error) => {
      console.error('Erro ao vincular produtos em lote:', error);
    },
  });
}

// ==================== PRODUCT QUERIES ====================

/**
 * Hook para listar produtos do estabelecimento
 */
export function useEstablishmentProducts(params?: EstablishmentProductSearchParams, options?: any) {
  return useQuery<EstablishmentProductListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS, params],
    queryFn: () => establishmentService.listProducts(params as EstablishmentProductSearchParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

export function useProductsAll(options?: any) {
  return useQuery<ProductListAllResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_PRODUCTS_ALL],
    queryFn: () => establishmentService.listAllProducts(),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para obter detalhes de um produto
 */
export function useProductDetail(params: EstabilishmentProductDetailsParams, options?: any) {
  return useQuery<EstablishmentProductDetailResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_PRODUCT_DETAIL, params],
    queryFn: () => establishmentService.getProductDetail(params),
    enabled: !!params.product_id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para obter sugestões de produtos do estabelecimento
 */
export function useEstablishmentProductSuggestions(params?: GlobalProductSearchParams) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_PRODUCT_SUGGESTIONS, params],
    queryFn: () => establishmentService.getProductSuggestions(params as GlobalProductSearchParams),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

/**
 * Hook para obter lista global de produtos
 */
export function useGlobalProductList() {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_GLOBAL_PRODUCT_LIST],
    queryFn: () => establishmentService.getGlobalProductList(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
}

// ==================== STOCK MUTATIONS ====================

export function useEstablishmentStock(params?: StockListParams, options?: any) {
  return useQuery<StockListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK],
    queryFn: () => establishmentService.listStock(params as StockListParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

/**
 * Hook para adicionar estoque
 */
export function useAddStock() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.ADD_STOCK],
    mutationFn: (data: AddStockRequest) => establishmentService.addStock(data),
    onSuccess: () => {
      // Invalidate stock list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK] });
    },
    onError: (error: Error) => {
      console.error('Erro ao adicionar estoque:', error);
    },
  });
}

/**
 * Hook para agendar transferência de estoque
 */
export function useStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.STOCK_TRANSFER],
    mutationFn: (data: StockTransferRequest) => establishmentService.stockTransfer(data),
    onSuccess: () => {
      // Invalidate stock transfers list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_TRANSFERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao agendar transferência de estoque:', error);
    },
  });
}

/**
 * Hook para agendar transferência de estoque
 */
export function useScheduleStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.SCHEDULE_STOCK_TRANSFER],
    mutationFn: (data: ScheduleStockTransferRequest) => establishmentService.scheduleStockTransfer(data),
    onSuccess: () => {
      // Invalidate stock transfers list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_TRANSFERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao agendar transferência de estoque:', error);
    },
  });
}

/**
 * Hook para executar transferência de estoque
 */
export function useExecuteStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.EXECUTE_STOCK_TRANSFER],
    mutationFn: (data: ExecuteStockTransferRequest) => establishmentService.executeStockTransfer(data),
    onSuccess: () => {
      // Invalidate stock and transfers lists
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_TRANSFERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao executar transferência de estoque:', error);
    },
  });
}

/**
 * Hook para cancelar transferência de estoque
 */
export function useCancelStockTransfer() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CANCEL_STOCK_TRANSFER],
    mutationFn: (data: CancelStockTransferRequest) => establishmentService.cancelStockTransfer(data),
    onSuccess: () => {
      // Invalidate stock movements list
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_MOVEMENTS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_TRANSFERS] });
    },
    onError: (error: Error) => {
      console.error('Erro ao cancelar transferência de estoque:', error);
    },
  });
}

export function useCreateStockEntry() {
  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_STOCK_ENTRY],
    mutationFn: (data: CreateStockEntryRequest) => establishmentService.createStockEntry(data),
  });
}

export function useCreateStockOut() {
  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_STOCK_OUT],
    mutationFn: (data: CreateStockOutRequest) => establishmentService.createStockOut(data),
  });
}

export function usePayPurchase() {
  const queryClient = useQueryClient();
  return useMutation<PayPurchaseResponse, Error, { establishment_id: string; data: PayPurchaseRequest }>({
    mutationKey: [ESTABLISHMENT_KEYS.PAY_PURCHASE],
    mutationFn: ({ establishment_id, data }) => establishmentService.payPurchase(establishment_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_MOVEMENTS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK] });
    },
  });
}

export function usePurchasePixInit() {
  return useMutation<PurchasePixInitResponse, Error, { establishment_id: string; data: PurchasePixInitRequest }>({
    mutationKey: [ESTABLISHMENT_KEYS.PURCHASE_PIX_INIT],
    mutationFn: ({ establishment_id, data }) => establishmentService.purchasePixInit(establishment_id, data),
  });
}

export function usePurchasePixConfirm() {
  const queryClient = useQueryClient();
  return useMutation<PurchasePixConfirmResponse, Error, { establishment_id: string; data: PurchasePixConfirmRequest }>({
    mutationKey: [ESTABLISHMENT_KEYS.PURCHASE_PIX_CONFIRM],
    mutationFn: ({ establishment_id, data }) => establishmentService.purchasePixConfirm(establishment_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_MOVEMENTS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK] });
    },
  });
}

export function usePurchasePaymentReceipt(
  params: { establishment_id: string; payment_id: string } | null,
  options?: any,
) {
  return useQuery<PaymentReceipt>({
    queryKey: [ESTABLISHMENT_KEYS.PURCHASE_PAYMENT_RECEIPT, params],
    queryFn: () => establishmentService.purchasePaymentReceipt(params!.establishment_id, params!.payment_id),
    enabled: !!(params?.establishment_id && params?.payment_id),
    staleTime: 5 * 60 * 1000,
    retry: false,
    ...options,
  });
}

// ==================== STOCK QUERIES ====================

/**
 * Hook para listar transferências de estoque
 */
export function useStockTransfers(params?: StockTransferParams) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_TRANSFERS, params],
    queryFn: () => establishmentService.listStockTransfers(params as StockTransferParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook para listar estoque
 */
export function useStock(params?: StockListParams, options?: any) {
  return useQuery<StockListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK, params],
    queryFn: () => establishmentService.listStock(params as StockListParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

export function useStockSummary(params?: StockSummaryParams, options?: any) {
  return useQuery<StockSummaryResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_SUMMARY, params],
    queryFn: () => establishmentService.listStockSummary(params as StockSummaryParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

export function useStockMovements(params?: StockMovementsParams, options?: any) {
  return useQuery<StockMovementsResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_MOVEMENTS, params],
    queryFn: () => establishmentService.listStockMovements(params as StockMovementsParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

export function useStockNFs(params?: StockNFsParams, options?: any) {
  return useQuery<StockNFsResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_NFS, params],
    queryFn: () => establishmentService.listStockNFs(params as StockNFsParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

export function useStockExpiration(params?: StockExpirationParams, options?: any) {
  return useQuery<StockExpirationResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_STOCK_EXPIRATION, params],
    queryFn: () => establishmentService.listStockExpiration(params as StockExpirationParams),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options, // Spread das opções adicionais
  });
}

// ==================== DASHBOARD QUERIES ====================

export function useDashboard(params?: GetDashboardDataParams) {
  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_DASHBOARD, params],
    queryFn: () => establishmentService.getDashboardData(params!),
    enabled: !!params,
  });
}

// ==================== BALANCE QUERIES ====================

export function useEstablishmentBalance(options?: any) {
  return useQuery<EstablishmentBalance>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_BALANCE],
    queryFn: () => establishmentService.getEstablishmentBalance(),
    ...options,
  });
}

/**
 * Hook para obter extrato do estabelecimento
 */
export function useEstablishmentExtract(params: EstablishmentExtractParams, options?: { enabled?: boolean }) {
  const enabled = params.establishmentId !== undefined && options?.enabled !== false;

  return useQuery({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_EXTRACT, params],
    queryFn: () => backofficeService.getEstablishmentExtract(params),
    enabled,
  });
}

export function useGetEstablishmentExtract(params?: GetEstablishmentExtractParams, options?: any) {
  return useQuery<GetEstablishmentExtractResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_EXTRACT, params],
    queryFn: () => establishmentService.getEstablishmentExtract(params as GetEstablishmentExtractParams),
    ...options,
  });
}

// ==================== SALES QUERIES ====================

export function useGetEstablishmentSales(params?: GetEstablishmentSalesQuery, options?: any) {
  return useQuery<GetEstablishmentSalesResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_SALES, params],
    queryFn: () => establishmentService.getEstablishmentSales(params as GetEstablishmentSalesQuery),
    enabled: !!params,
    ...options,
  });
}

// ==================== LUNCHBOX QUERIES ====================

export function useEstablishmentLunchboxDashboard(params?: EstablishmentLunchboxDashboardParams, options?: any) {
  return useQuery<EstablishmentLunchboxDashboard>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_LUNCHBOX_DASHBOARD, params],
    queryFn: () => establishmentService.getEstablishmentLunchboxDashboard(params as EstablishmentLunchboxDashboardParams),
    enabled: !!params,
    retry: false,
    ...options,
  });
}

export function useEstablishmentLunchboxClassDetail(params?: EstablishmentLunchboxClassDetailParams, options?: any) {
  return useQuery<EstablishmentLunchboxClassDetail>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_LUNCHBOX_CLASS_DETAIL, params],
    queryFn: () => establishmentService.getEstablishmentLunchboxClassDetail(params as EstablishmentLunchboxClassDetailParams),
    enabled: !!params?.establishment_id && !!params?.interval_id && !!params?.class_id && !!params?.date,
    retry: false,
    ...options,
  });
}

/**
 * Busca o detalhe da turma em vários intervalos de uma vez.
 * Usa a mesma queryKey do hook singular, então o cache é compartilhado.
 */
export function useEstablishmentLunchboxClassDetails(paramsList?: EstablishmentLunchboxClassDetailParams[]) {
  return useQueries({
    queries: (paramsList ?? []).map((params) => ({
      queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_LUNCHBOX_CLASS_DETAIL, params],
      queryFn: () => establishmentService.getEstablishmentLunchboxClassDetail(params),
      enabled: !!params.establishment_id && !!params.interval_id && !!params.class_id && !!params.date,
      retry: false,
    })),
  });
}

// ==================== WITHDRAWS QUERIES ====================

export function useGetEstablishmentWithdrawsHistory(params?: GetEstablishmentWithdrawsHistoryParams, options?: any) {
  return useQuery<GetEstablishmentWithdrawsHistoryResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_WITHDRAWS_HISTORY, params],
    queryFn: () => establishmentService.getEstablishmentWithdrawsHistory(params as GetEstablishmentWithdrawsHistoryParams),
    enabled: !!params,
    ...options,
  });
}

// ==================== CASH FLOW QUERIES ====================

export function useGetEstablishmentCashFlowSummary(params?: GetEstablishmentCashFlowSummaryParams, options?: any) {
  return useQuery<GetEstablishmentCashFlowSummaryResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_CASH_FLOW_SUMMARY, params],
    queryFn: () => establishmentService.getEstablishmentCashFlowSummary(params as GetEstablishmentCashFlowSummaryParams),
    enabled: !!params && !!params.period,
    ...options,
  });
}

export function useGetEstablishmentCashFlowMovements(params?: GetEstablishmentCashFlowMovementsParams, options?: any) {
  return useQuery<GetEstablishmentCashFlowMovementsResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_CASH_FLOW_MOVEMENTS, params],
    queryFn: () => establishmentService.getEstablishmentCashFlowMovements(params as GetEstablishmentCashFlowMovementsParams),
    enabled: !!params && !!params.period,
    ...options,
  });
}

export function usePostEstablishmentWithdraws(options?: any) {
  return useMutation({
    mutationFn: (body: PostEstablishmentWithdrawsDto) => establishmentService.postEstablishmentWithdraws(body),
    ...options,
  });
}

export function usePostWithdrawInit(options?: any) {
  return useMutation<WithdrawInitResponse, Error, WithdrawInitDto>({
    mutationFn: (body: WithdrawInitDto) => establishmentService.postWithdrawInit(body),
    ...options,
  });
}

export function usePostWithdrawConfirm(options?: any) {
  return useMutation<WithdrawConfirmResponse, Error, WithdrawConfirmDto>({
    mutationFn: (body: WithdrawConfirmDto) => establishmentService.postWithdrawConfirm(body),
    ...options,
  });
}

export function usePostWithdrawDelete(options?: any) {
  return useMutation<WithdrawDeleteResponse, Error, WithdrawDeleteDto>({
    mutationFn: (body: WithdrawDeleteDto) => establishmentService.postWithdrawDelete(body),
    ...options,
  });
}

// ==================== ORDERS REPORTS QUERIES ====================

/**
 * Hook para obter relatório geral de pedidos
 */
export function useOrdersReportOverall(params?: GetOrdersReportOverallParams, options?: any) {
  return useQuery<GetOrdersReportOverallResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ORDERS_REPORT_OVERALL, params],
    queryFn: () => establishmentService.getOrdersReportOverall(params as GetOrdersReportOverallParams),
    enabled: !!params?.establishment_id && !!params?.date,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook para obter relatório de pedidos por intervalo
 */
export function useOrdersReportOverallByInterval(params?: GetOrdersReportOverallByIntervalParams, options?: any) {
  return useQuery<GetOrdersReportOverallByIntervalResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ORDERS_REPORT_OVERALL_BY_INTERVAL, params],
    queryFn: () => establishmentService.getOrdersReportOverallByInterval(params as GetOrdersReportOverallByIntervalParams),
    enabled: !!params?.establishment_id && !!params?.date,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook para obter relatório de pedidos por atendente
 */
export function useOrdersReportByAttendant(params?: GetOrdersReportByAttendantParams, options?: any) {
  return useQuery<GetOrdersReportByAttendantResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ORDERS_REPORT_BY_ATTENDANT, params],
    queryFn: () => establishmentService.getOrdersReportByAttendant(params as GetOrdersReportByAttendantParams),
    enabled: !!params?.establishment_id && !!params?.date,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook para obter relatório de % de pedidos abaixo do tempo alvo
 */
export function useOrdersReportPctUnderTarget(params?: GetOrdersReportPctUnderTargetParams, options?: any) {
  return useQuery<GetOrdersReportPctUnderTargetResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ORDERS_REPORT_PCT_UNDER_TARGET, params],
    queryFn: () => establishmentService.getOrdersReportPctUnderTarget(params as GetOrdersReportPctUnderTargetParams),
    enabled: !!params?.establishment_id && !!params?.date && params?.tempo !== undefined,
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

// ==================== TOTEM BILLING QUERIES ====================

export function useEstablishmentTotemBillingSummary(year?: string, options?: any) {
  return useQuery<EstablishmentTotemBillingSummary>({
    queryKey: [ESTABLISHMENT_KEYS.GET_ESTABLISHMENT_TOTEM_BILLING_SUMMARY, year],
    queryFn: () => establishmentService.getEstablishmentTotemBillingSummary(year),
    staleTime: 2 * 60 * 1000, // 2 minutes
    ...options,
  });
}

/**
 * Hook para criar estabelecimento a partir do admin
 */
export function useCreateEstabilishmentFromAdmin(options?: any) {
  return useMutation({
    mutationFn: (data: CreateEstablishmentFromAdminRequest) => establishmentService.createEstablishmentFromAdmin(data),
    ...options,
  });
}

// ==================== TAX INTEGRATION ====================

/**
 * Hook para obter o status da integração fiscal
 */
export function useTaxIntegrationStatus(establishmentId: string, enabled: boolean = true) {
  return useQuery<TaxIntegrationStatus>({
    queryKey: [ESTABLISHMENT_KEYS.GET_TAX_INTEGRATION_STATUS, establishmentId],
    queryFn: () => establishmentService.getTaxIntegrationStatus(establishmentId),
    enabled: !!establishmentId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook para obter a configuração fiscal
 */
export function useTaxConfig(establishmentId: string, enabled: boolean = true) {
  return useQuery<TaxConfigResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_TAX_CONFIG, establishmentId],
    queryFn: () => establishmentService.getTaxConfig(establishmentId),
    enabled: !!establishmentId && enabled,
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: false, // Não tentar novamente se falhar - evita bloqueio quando CRT não está configurado
  });
}

/**
 * Hook para obter a lista de CSTs
 */
export function useTaxCstList(enabled: boolean = true) {
  return useQuery<TaxCstListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.GET_TAX_CST_LIST],
    queryFn: () => establishmentService.getTaxCstList(),
    enabled: enabled,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook para obter a lista de PIS/COFINS
 */
export function usePisCofinsList(enabled: boolean = true) {
  return useQuery<TaxOption[]>({
    queryKey: [ESTABLISHMENT_KEYS.GET_TAX_CST_LIST, 'pis-cofins'],
    queryFn: () => establishmentService.getPisCofinsList(),
    enabled: enabled,
    staleTime: 60 * 60 * 1000, // 1 hour
  });
}

/**
 * Hook para habilitar integração fiscal (NFe)
 */
export function useEnableTaxIntegration(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EnableTaxIntegrationRequest) => establishmentService.enableTaxIntegration(data),
    onSuccess: () => {
      // Invalidate tax integration status query
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_TAX_INTEGRATION_STATUS] });
    },
    ...options,
  });
}

/**
 * Hook para configurar integração fiscal (NFe)
 */
export function useConfigureTaxIntegration(options?: any) {
  return useMutation({
    mutationFn: (data: ConfigureTaxIntegrationRequest) => establishmentService.configureTaxIntegration(data),
    ...options,
  });
}

/**
 * Hook para deletar integração fiscal (NFe)
 */
export function useDeleteTaxIntegration(options?: any) {
  return useMutation({
    mutationFn: (establishmentId: string) => establishmentService.deleteTaxIntegration(establishmentId),
    ...options,
  });
}

/**
 * Hook para deletar certificado fiscal
 */
export function useDeleteTaxCertificate(options?: any) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: DeleteTaxCertificateRequest) => establishmentService.deleteTaxCertificate(data),
    onSuccess: () => {
      // Invalidate tax integration status query
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_TAX_INTEGRATION_STATUS] });
    },
    ...options,
  });
}

/**
 * Hook para fazer upload de certificado fiscal
 */
export function useUploadTaxCertificate(options?: any) {
  return useMutation({
    mutationFn: (data: UploadTaxCertificateRequest) => establishmentService.uploadTaxCertificate(data),
    ...options,
  });
}

/**
 * Hook para obter informações da conta digital (Paytime)
 * GET /establishment/paytime/info?establishment_id=UUID
 */
export function usePaytimeInfo(establishmentId: string, enabled: boolean = true, refetchInterval?: number | false) {
  return useQuery<PaytimeInfo>({
    queryKey: [ESTABLISHMENT_KEYS.GET_PAYTIME_INFO, establishmentId],
    queryFn: () => establishmentService.getPaytimeInfo(establishmentId),
    enabled: enabled && !!establishmentId,
    staleTime: 30 * 1000,
    refetchOnMount: 'always',
    refetchInterval,
  });
}

/**
 * Hook para criar conta digital (Paytime)
 * POST /establishment/paytime/create
 */
export function useCreatePaytimeAccount() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (establishmentId: string) => establishmentService.createPaytimeAccount(establishmentId),
    onSuccess: (_data, establishmentId) => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_PAYTIME_INFO, establishmentId] });
    },
  });
}

export function useActivatePaytimeSub() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (establishmentId: string) => establishmentService.activatePaytimeSub(establishmentId),
    onSuccess: (_data, establishmentId) => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_PAYTIME_INFO, establishmentId] });
    },
  });
}

// ==================== SUPPLIER HOOKS ====================

export function useSuppliers(params: SupplierListParams, options?: any) {
  return useQuery<SupplierListResponse>({
    queryKey: [ESTABLISHMENT_KEYS.LIST_SUPPLIERS, params],
    queryFn: () => establishmentService.listSuppliers(params),
    enabled: !!params.establishment_id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useGetSupplier(establishment_id: string, id: string, options?: any) {
  return useQuery<Supplier>({
    queryKey: [ESTABLISHMENT_KEYS.GET_SUPPLIER, establishment_id, id],
    queryFn: () => establishmentService.getSupplier(establishment_id, id),
    enabled: !!establishment_id && !!id,
    staleTime: 2 * 60 * 1000,
    ...options,
  });
}

export function useCreateSupplier(options?: any) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.CREATE_SUPPLIER],
    mutationFn: ({ establishment_id, data }: { establishment_id: string; data: CreateSupplierRequest }) =>
      establishmentService.createSupplier(establishment_id, data),
    onSuccess: (_data, { establishment_id }) => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_SUPPLIERS, { establishment_id }] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_SUPPLIERS] });
    },
    ...options,
  });
}

export function useUpdateSupplier(options?: any) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [ESTABLISHMENT_KEYS.UPDATE_SUPPLIER],
    mutationFn: ({ establishment_id, data }: { establishment_id: string; data: UpdateSupplierRequest }) =>
      establishmentService.updateSupplier(establishment_id, data),
    onSuccess: (_data, { establishment_id }) => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_SUPPLIERS] });
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.GET_SUPPLIER, establishment_id] });
    },
    ...options,
  });
}

export function useDeleteSupplier(options?: any) {
  const queryClient = useQueryClient();
  return useMutation<DeleteSupplierResponse, Error, { establishment_id: string; data: DeleteSupplierRequest }>({
    mutationKey: [ESTABLISHMENT_KEYS.DELETE_SUPPLIER],
    mutationFn: ({ establishment_id, data }) => establishmentService.deleteSupplier(establishment_id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [ESTABLISHMENT_KEYS.LIST_SUPPLIERS] });
    },
    ...options,
  });
}
